import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * The first design-system primitive, and the pattern every other one follows:
 * CVA for variants, Radix Slot for composition, forwardRef for focus
 * management, `cn` last so callers can override.
 *
 * ── Visual refresh (post-migration) ─────────────────────────────
 *
 * Box dimensions (height, padding, font-size per size step) are unchanged
 * from the original .btn-* reproduction, deliberately — a handful of legacy
 * screens still embed their own .btn-primary buttons next to this component
 * (ConsultationChat.jsx inside the migrated Consultation shell, for one), and
 * a visibly different height or hit target between the two reads as a bug,
 * not as progress. Color, border and elevation are free to move, following
 * the PulseOS-referenced redesign (see HMS_UI_REDESIGN_ANALYSIS.md) — a
 * subtle shadow on the affirmative variants, tokens instead of one hardcoded
 * hex for the ghost border.
 *
 * Colours come from Tailwind tokens that resolve to the runtime branding vars
 * (see src/styles/theme.css) — `bg-teal` follows whatever colour this hospital
 * configured. Never hardcode a brand hex here.
 */
const buttonVariants = cva(
  // Base: shared by every variant. `active:scale-[.98]` and the disabled
  // treatment mirror index.css's bare `button` rules so migrated and legacy
  // buttons feel identical under the finger.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius)] ' +
    'font-sans font-medium transition-[opacity,transform,background-color,box-shadow] duration-150 ' +
    'active:scale-[.98] disabled:pointer-events-none disabled:opacity-50 ' +
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25 ' +
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** .btn-primary — the single affirmative action on a screen. */
        primary: 'bg-teal text-white shadow-sm hover:opacity-[.88]',
        /** .btn-ghost — secondary/cancel. */
        ghost: 'bg-transparent text-ink-muted border border-border hover:bg-muted',
        /** .btn-success — completing a clinical step (approve, discharge). */
        success: 'bg-success text-white shadow-sm hover:opacity-[.88]',
        /**
         * .btn-danger — destructive. Note this is intentionally the *only*
         * variant that is not brand-tinted: a hospital branding itself red
         * must not make every primary button look destructive.
         */
        danger: 'bg-danger text-white shadow-sm hover:opacity-[.88]',
        /** Text-only, for inline actions inside dense tables. */
        link: 'bg-transparent text-teal underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        /** Matches .btn-primary's 9px 20px at the app's 13px base. */
        md: 'h-9 px-5 text-[13px]',
        lg: 'h-11 px-7 text-sm',
        /** Square, for a lone icon. */
        icon: 'h-9 w-9 p-0',
      },
      full: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', full: false },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render as the child element instead of a <button> — for a link that should
   * look like a button, without nesting an <a> inside a <button>.
   */
  asChild?: boolean;
  /** Shows a spinner and blocks interaction. Implies disabled. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, full }), className)}
        // A loading button that is still clickable is how duplicate
        // prescriptions and duplicate invoices get created — mutations here are
        // deliberately never auto-retried for the same reason (see
        // src/lib/queryClient.ts).
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <span
            aria-hidden="true"
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        )}
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
